import { useEffect, useRef, useState } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import {
  Button,
  ButtonTypeEnum,
  Dialog,
  DialogContent,
  Loading,
} from "@tenant/ui";
import styles from "./GridV2OptOutModal.module.scss";

export interface GridV2OptOutModalProps {
  open: boolean;
  /** "Continue with refreshed pricing", the X, Escape and the overlay: nothing changes. */
  onClose: () => void;
  /**
   * Records that this user leaves the Grid V2 test (opt-out endpoint, tracking,
   * re-shop live in the caller). The dialog stays busy until it resolves, closes
   * on resolve, and shows a retryable error on reject.
   */
  onOptOut: () => Promise<void>;
}

type OptOutStatus = "idle" | "pending" | "failed";

/**
 * Confirmation before a Grid V2 test participant returns to the original grid
 * pricing. The opt-out is permanent (RBC legal), so the dialog says so in one
 * sentence and cannot be dismissed while the opt-out is being recorded.
 */
export const GridV2OptOutModal = ({
  open,
  onClose,
  onOptOut,
}: GridV2OptOutModalProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "GridV2OptOutModal");
  const [optOutStatus, setOptOutStatus] = useState<OptOutStatus>("idle");
  const isPending = optOutStatus === "pending";
  // Counts opt-out attempts so a call still in flight after the owner closed
  // the dialog (open=false) cannot write its outcome into a reopened dialog.
  const attemptRef = useRef(0);

  // A reopened dialog starts clean, without the previous attempt's error.
  useEffect(() => {
    if (!open) {
      attemptRef.current += 1;
      setOptOutStatus("idle");
    }
  }, [open]);

  const handleOptOut = async () => {
    const attempt = ++attemptRef.current;
    setOptOutStatus("pending");
    try {
      await onOptOut();
    } catch {
      if (attempt === attemptRef.current) setOptOutStatus("failed");
      return;
    }
    if (attempt !== attemptRef.current) return;
    setOptOutStatus("idle");
    onClose();
  };

  return (
    <Dialog
      open={open}
      // X, Escape and the overlay end here; blocked while the opt-out is being recorded.
      onClose={() => {
        if (!isPending) onClose();
      }}
    >
      <DialogContent className={block} title={t("gridV2.optOutModal.title")}>
        {/* <0> wraps the bold "you won't be able to switch" segment. */}
        <p className={cn("body")}>
          <Trans i18nKey="gridV2.optOutModal.body" components={[<strong />]} />
        </p>

        <div className={cn("actions-area")}>
          <div className={cn("actions")}>
            {/* Primary first in the DOM: on top when stacked, right in a row (row-reverse). */}
            <Button
              buttonType={ButtonTypeEnum.Primary}
              className={cn("action")}
              onClick={onClose}
              disabled={isPending}
            >
              {t("gridV2.optOutModal.continueWithRefreshedPricing")}
            </Button>
            <Button
              buttonType={ButtonTypeEnum.Tertiary}
              className={cn("action", { optOut: true })}
              onClick={handleOptOut}
              disabled={isPending}
            >
              {t("gridV2.optOutModal.continueWithOriginalPricing")}
            </Button>
            {isPending && (
              <Loading
                className={cn("spinner")}
                label={t("gridV2.optOutModal.submitting")}
              />
            )}
          </div>

          {optOutStatus === "failed" && (
            <p className={cn("error")} role="alert">
              {t("gridV2.optOutModal.optOutFailed")}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
